import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy, Inject, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment-timezone';
import { AlertService } from 'src/app/core/services/alert.service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { UserProfile } from 'src/app/pages/my-account/interfaces/company-settings.interface';
import { MyAccountService } from 'src/app/pages/my-account/services/my-acount.service';
import { SummaryData } from 'src/app/pages/new-service/interfaces/SummaryData';
import { FunnelService, UploadResponse } from 'src/app/pages/new-service/services/funnel.service';
import { InfoModalComponent } from 'src/app/shared/info-modal/info-modal.component';
import { DeleteFileModalComponent } from 'src/app/pages/new-service/components/new-service-vehicle/dialog/delete-file-modal/delete-file-modal.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';


export enum DocumentType {
    Document = 1,
    Link = 2,
}

interface PdfDocument {
    pdfId: string;
    type: DocumentType;
    name: string;
    link?: string;
    fileSize?: string;
    uploadedDate?: string;
    checked: boolean;
    isNew: boolean;
    /** false si se guardó para próximos servicios. */
    isTemporal?: boolean;
}

@Component({
    selector: 'app-modal-upload-document-link',
    templateUrl: './modal-upload-document-link.component.html',
    styleUrls: ['./modal-upload-document-link.component.scss'],
})


export class ModalUploadDocumentLinkComponent implements OnInit, OnDestroy {

    @Input() promotionCode = '';
    @Output() promotionCodeChange = new EventEmitter<string>();
    @Input() promotionCodeValidator = false;
    @Output() validatePromoCode = new EventEmitter<void>();
    @ViewChild('closeModal') closeModal: ElementRef | undefined;
    @ViewChild('searchInput') searchInput!: ElementRef;

    searchValue = '';
    currentTab: number = 0;

    link_url: string = '';
    link_name: string = '';

    saveAsPermanentFile = false;
    saveAsPermanentLink = false;

    documents: PdfDocument[] = [];
    originalDocuments: any[] = [];
    protected files: File[] = [];
    protected rejected: File[] = [];

    fileLimitExceeded = false;
    maxFiles = 15;

    showErrorAlert = false;
    textAlert = '';

    addedLinks: PdfDocument[] = [];
    addedFiles: PdfDocument[] = [];
    private isSubmitting = false;

    private destroyed$ = new Subject<void>();

    get clientId(): string | undefined {
        return this.data.clientId || this.myAccountService.getUserDetailLocal()?.UserId;
    }

    constructor(private dialogRef: MatDialogRef<InfoModalComponent>,
        private dialog: MatDialog,
        private myAccountService: MyAccountService,
        private funnelService: FunnelService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private loader: LoaderService,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: { summaryData: SummaryData; orderHash?: string; clientId?: string }) { }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    async ngOnInit() {
        this.loader.show();
        try {
            const UserAccount: UserProfile | null = this.myAccountService.getUserDetailLocal();
            if (UserAccount) {
                this.setUserData(UserAccount)
            } else {
                this.myAccountService.getUserDetail().pipe(takeUntil(this.destroyed$)).subscribe({
                    next: (data: UserProfile) => {
                        this.myAccountService.setUserDetailLocal(data);
                        this.setUserData(data)
                    }
                })
            }
        }
        catch {
            console.error("Error on get UserProfile")
        }
    }


    setUserData(userDetails: UserProfile) {
        if (userDetails?.UserId) {
            this.funnelService.getAttachedFilesAndLinks(userDetails?.UserId).pipe(takeUntil(this.destroyed$)).subscribe({
                next: (response) => {
                    this.documents = response.map((doc: any) => {
                        return {
                            pdfId: doc.Id,
                            type: doc.PdfUrl ? DocumentType.Link : DocumentType.Document,
                            name: doc.PdfName,
                            link: doc.PdfUrl,
                            fileSize: doc.Size ? (doc.Size).toFixed(2) + ' MB' : undefined,
                            uploadedDate: doc.CreationDate ? moment(doc.CreationDate).format('DD/MM/YYYY') : undefined,
                            checked: false,
                            isNew: false,
                            // /attachedfile/all solo devuelve los permanentes.
                            isTemporal: false
                        };
                    });
                    this.originalDocuments = [...this.documents];
                    this.loader.hide();
                },
                error: (error) => {
                    this.textAlert = this.translateService.instant('error_fetching_documents');
                    this.showErrorAlert = true;
                    this.alertService.setShowAlert(true);
                    this.loader.hide();
                }
            });
        }
    }

    private get hasOneFileAlready(): boolean {
        const checkedCount = this.originalDocuments.filter((doc) => doc.checked).length;
        return checkedCount > 0 || this.files.length > 0 || this.addedFiles.length > 0 || this.addedLinks.length > 0;
    }

    private showInModalAlert(text: string): void {
        this.showErrorAlert = false;
        this.cdr.detectChanges();
        this.textAlert = text;
        this.showErrorAlert = true;
    }

    disableButton() {
        return this.documents.every((doc) => doc.checked === false) && this.files.length === 0 && !this.link_url && !this.link_name;
    }

    onSearchChange() {
        this.searchValue = this.searchInput.nativeElement.value.toLowerCase();
        this.originalDocuments.forEach((doc) => doc.checked = false);
        this.documents = this.originalDocuments.filter((doc) => doc.name.toLowerCase().includes(this.searchValue) || (doc.link && doc.link.toLowerCase().includes(this.searchValue)));
    }
    clearFilter() {
        this.searchInput.nativeElement.value = '';
        this.searchValue = '';
        this.originalDocuments.forEach((doc) => doc.checked = false);
        this.documents = [...this.originalDocuments];
    }

    // Nada se sube hasta pulsar "Subir archivos", así que cancelar no deja
    // ficheros huérfanos que haya que borrar del backend.
    cancel() {
        this.dialogRef.close();
    }

    isDocumentBlocked(doc: PdfDocument): boolean {
        return !doc.checked && this.hasOneFileAlready;
    }

    toggleDocumentCheck(doc: PdfDocument): void {
        if (this.isDocumentBlocked(doc)) {
            this.showInModalAlert(this.translateService.instant('only_one_file_allowed'));
            return;
        }
        doc.checked = !doc.checked;
    }

    deleteSavedFile(doc: PdfDocument, event: MouseEvent): void {
        // La fila entera alterna la selección; la papelera no debe hacerlo.
        event.stopPropagation();

        this.dialog.open(DeleteFileModalComponent, {
            data: { pdfId: doc.pdfId, deleteFromBackend: true, clientId: this.clientId },
            width: '600px',
            height: '300px',
            panelClass: ['modal-dialog-centered', 'custom-overlay-zindex', 'modal-class-template-500'],
        }).afterClosed().subscribe((deleted: boolean) => {
            if (!deleted) {
                return;
            }
            this.originalDocuments = this.originalDocuments.filter((d) => d.pdfId !== doc.pdfId);
            this.documents = this.documents.filter((d) => d.pdfId !== doc.pdfId);
        });
    }

    async submit() {
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;
        try {
            await this.uploadAndClose();
        }
        finally {
            this.isSubmitting = false;
        }
    }

    private async uploadAndClose() {
        this.loader.show();
        if (this.link_name && this.link_url) {
            if (this.files.length > 0 || this.addedFiles.length > 0 || this.documents.some((doc) => doc.checked)) {
                this.loader.hide();
                this.textAlert = this.translateService.instant('only_one_file_allowed');
                this.showErrorAlert = true;
                this.alertService.setShowAlert(true);
                return;
            }

            if (!this.isValidUrlProtocol(this.link_url)) {
                this.loader.hide();
                this.textAlert = this.translateService.instant('invalid_url_protocol');
                this.showErrorAlert = true;
                this.alertService.setShowAlert(true);
                return;
            }
            try {
                const resultLink = await firstValueFrom(this.funnelService.uploadFileOrLinkV2(!this.saveAsPermanentLink, this.link_name, this.link_url, undefined, this.clientId).pipe(takeUntil(this.destroyed$))) as UploadResponse;
                if (resultLink) {
                    this.addedLinks.push({
                        pdfId: resultLink.Id,
                        name: resultLink.PdfName,
                        link: resultLink.PdfUrl,
                        type: DocumentType.Link,
                        checked: false,
                        isNew: true,
                        isTemporal: resultLink.IsTemporal ?? !this.saveAsPermanentLink
                    });
                    if (!this.data.summaryData.pdfIds) {
                        this.data.summaryData.pdfIds = [];
                    }
                    this.data.summaryData.pdfIds.push(resultLink.Id);
                }
            }
            catch (error: any) {
                this.loader.hide();
                this.textAlert = this.uploadErrorMessage(error, this.link_name, 'error_link_with_same_name', 'error_uploading_link');
                this.showErrorAlert = true;
                this.alertService.setShowAlert(true);
                return;
            }
        }
        // El fichero se sube aquí y no al soltarlo, para que "Guardar archivos
        // para próximos servicios" se lea con el valor que el usuario deja puesto.
        else if (this.files.length > 0) {
            const fileToUpload = this.files[0];
            try {
                const resultUpload = await firstValueFrom(this.funnelService.uploadFileOrLinkV2(!this.saveAsPermanentFile, fileToUpload.name, undefined, fileToUpload, this.clientId).pipe(takeUntil(this.destroyed$))) as UploadResponse;
                this.addedFiles.push({
                    pdfId: resultUpload.Id,
                    name: resultUpload.PdfName,
                    fileSize: (fileToUpload.size / (1024 * 1024)).toFixed(2) + ' MB',
                    type: DocumentType.Document,
                    checked: false,
                    isNew: true,
                    isTemporal: resultUpload.IsTemporal ?? !this.saveAsPermanentFile,
                });
                if (!this.data.summaryData.pdfIds) {
                    this.data.summaryData.pdfIds = [];
                }
                this.data.summaryData.pdfIds.push(resultUpload.Id);
            }
            catch (error: any) {
                this.loader.hide();
                this.textAlert = this.uploadErrorMessage(error, fileToUpload.name, 'error_file_with_same_name', 'error_uploading_file');
                this.showErrorAlert = true;
                this.alertService.setShowAlert(true);
                console.error('Error al subir el archivo', error);
                return;
            }
        }
        const checkedDocuments = this.documents.filter((doc) => doc.checked);
        if (checkedDocuments.length > 0) {
            if (!this.data.summaryData.pdfIds) {
                this.data.summaryData.pdfIds = [];
            }
            checkedDocuments.forEach((doc) => {
                this.data.summaryData!.pdfIds!.push(doc.pdfId);
            });
        }
        this.loader.hide();

        this.dialogRef.close({
            selectedDocuments: checkedDocuments,
            addedFiles: this.addedFiles,
            addedLinks: this.addedLinks,
            summaryData: this.data.summaryData,
        });
    }

    onRemove(remove: File): void {
        this.files = this.files.filter((file) => file !== remove);
        this.rejected = this.rejected.filter((file) => file !== remove);
    }

    onChange(files: File[]) {

        let newFiles = files.filter((file) => !this.rejected.includes(file));
        newFiles = newFiles.filter((file) => !this.files.includes(file));

        if (newFiles.length === 0)
            return;

        if (this.hasOneFileAlready) {
            this.showInModalAlert(this.translateService.instant('only_one_file_allowed'));
            return;
        }

        this.files.push(newFiles[0]);
    }

    onReject(rejected: File[]): void {
        this.rejected = rejected;
    }

    onErrorAlertClosed() {
        this.showErrorAlert = false;
        this.textAlert = '';
    }
    private isValidUrlProtocol(url: string): boolean {
        return url.startsWith('http://') || url.startsWith('https://');
    }

    /**
     * El backend devuelve 409 tanto cuando ya existe un fichero con ese nombre
     * como cuando se ha llegado al tope de ficheros guardados, y lo único que
     * los distingue es el cuerpo de la respuesta.
     */
    private isStoredFilesLimitError(error: any): boolean {
        const body = error?.error;
        return typeof body === 'string' && body.toLowerCase().includes('limit of stored files');
    }

    private uploadErrorMessage(error: any, name: string, duplicateNameKey: string, fallbackKey: string): string {
        if (this.isStoredFilesLimitError(error)) {
            return this.translateService.instant('error_files_limit_reached');
        }
        if (error?.status === 409) {
            return this.translateService.instant(duplicateNameKey) + ' \'' + name + '\'';
        }
        if (error?.error?.error) {
            return error.error.error;
        }
        return this.translateService.instant(fallbackKey);
    }
}
